import React, { Suspense } from "react";
import { Route, Switch } from "react-router-dom";
import Home from "./containers/Home";
import NotFound from "./containers/NotFound";
import AppliedRoute from "./components/AppliedRoute";
import { UserClient } from "./api/apolloClient";
import { AdminGetUsers, AdminGetUser, UpdateUser } from "./api/user";
import {
  CreateUserGroup,
  GetUserGroup,
  GetUserGroups,
  UpdateUserGroup,
  SetUserGroupParent,
  DeleteUserGroup,
  addUser,
  removeUser,
} from "./api/usergroup";
import SystemEventView from "./containers/connx/SystemEventView";
import HelpRoutes from "./routes/HelpRoutes";
import UserRoutes from "./routes/UserRoutes";
import { ApolloProvider } from "@apollo/react-hooks";
import DCX from "./routes/DCX";
import { useUser } from "./userContext";
import { Loading } from "./components";
import Login from "./containers/Login";
import HeatMap from "./containers/maps/HeatMap";
import ConfigurationView from "./containers/connx/ConfigurationView";
import AdminUserList from "./containers/admin/AdminUserList";
import AdminUserGroupList from "./containers/admin/AdminUserGroupList";
import EditUserGroup from "./containers/admin/EditUserGroup";
import AdminuserEdit from "./containers/admin/AdminUserEdit";
import FirmwareView from "./containers/connx/FirmwareView";
import RegisteredDevicesView from "./containers/connx/RegisteredDevicesView";
import DeviceConfigurationEdit from "./containers/connx/DeviceConfigurationEdit";
import EditRegisteredDevice from "./containers/connx/EditRegisteredDevice";
import EditDefaultConfig from "./containers/connx/registeredDevice/EditDefaultConfig";
import RVModelView from "./containers/connx/rvmodel/RVModelView";
import RVModelEdit from "./containers/connx/rvmodel/RVModelEdit";
import RVModelOrderEdit from "./containers/connx/rvmodel/RVModelOrderEdit";

// Route components MUST be declared at module scope, not inline in the route
// table below. AppliedRoute renders `component` as a component *type*, so an
// inline arrow gets a fresh identity on every render of Routes and React
// unmounts and remounts the entire page - refetching its data. That used to
// fire doomed queries at sign-out, while the Cognito session was being torn
// down.

const HomeRoute = () => {
  const { user } = useUser();
  return <Home userID={user.id} isAuthenticated={user.isAuthenticated} />;
};

const AdminUsersRoute = ({ history, ...childProps }) => (
  <AdminUserList
    {...childProps}
    getUsers={AdminGetUsers}
    onEditUser={(user) => {
      history.push(`/admin/users/edit/${user.id}`);
    }}
  />
);

const AdminUserEditRoute = ({
  match: {
    params: { userId = "" },
  },
  ...childProps
}) => (
  <ApolloProvider client={UserClient}>
    <AdminuserEdit
      {...childProps}
      userId={userId}
      GetUser={async (userId) => {
        const r = await AdminGetUser(userId);
        return r.user;
      }}
      UpdateUser={UpdateUser}
    />
  </ApolloProvider>
);

const AdminUserGroupsRoute = ({ history, ...childProps }) => (
  <AdminUserGroupList
    {...childProps}
    getItems={GetUserGroups}
    deleteItem={DeleteUserGroup}
    createItem={CreateUserGroup}
    updateItem={UpdateUserGroup}
    onEdit={(id) => history.push(`/admin/usergroups/edit/${id}`)}
  />
);

const EditUserGroupRoute = ({
  history,
  match: {
    params: { groupId = "" },
  },
  ...childProps
}) => (
  <EditUserGroup
    {...childProps}
    groupId={groupId}
    onSaveUserGroupDetails={UpdateUserGroup}
    getUserGroup={GetUserGroup}
    getGroupUsers={(groupId, nextKey) => {
      return AdminGetUsers({ groupID: groupId, nextKey });
    }}
    findUsers={({ filter }) => {
      return AdminGetUsers({ filterUsernameLike: filter });
    }}
    addUser={addUser}
    removeUser={removeUser}
    setUserGroupParent={SetUserGroupParent}
    onEditGroup={(id) => history.push(`/admin/usergroups/edit/${id}`)}
    onDone={() => {
      history.goBack();
    }}
  />
);

const RegisteredDevicesRoute = ({ history, ...childProps }) => (
  <RegisteredDevicesView
    onEditDevice={(id) => history.push("/connx/devices/edit/" + id)}
    onAssignDeviceConfig={(id) =>
      history.push("/connx/devices/edit/defaultConfig/" + id)
    }
    {...childProps}
  />
);

const EditRegisteredDeviceRoute = ({
  history,
  match: {
    params: { id = "" },
  },
}) => (
  <EditRegisteredDevice
    id={id}
    onDone={(e) => {
      if (e) {
        console.log(e);
        alert("Failed to load");
      }
      history.goBack();
    }}
  />
);

const EditDefaultConfigRoute = ({
  history,
  match: {
    params: { id = "" },
  },
}) => <EditDefaultConfig id={id} done={() => history.goBack()} />;

const CloneDeviceConfigurationRoute = ({
  history,
  match: {
    params: { groupid = "", cfgid = "", versionid = "" },
  },
}) => (
  <DeviceConfigurationEdit
    groupID={groupid}
    cfgID={cfgid}
    versionID={versionid}
    onDone={() => {
      history.goBack();
    }}
    onError={(e) => {
      console.log(e);
      alert("Failed to load");
      history.goBack();
    }}
  />
);

const RVModelEditRoute = ({
  history,
  match: {
    params: { groupid = "", modelid = "" },
  },
}) => (
  <RVModelEdit
    groupID={groupid}
    modelID={modelid}
    onDone={() => history.goBack()}
  />
);

const RVModelOrderEditRoute = ({
  history,
  match: {
    params: { modelid = "", orderid = "" },
  },
}) => (
  <RVModelOrderEdit
    modelID={modelid}
    orderID={orderid}
    onDone={() => history.goBack()}
  />
);

// FIXME: break-up this into sub-route components (e.g. HelpRoutes.js)
const Routes = ({ childProps }) => {
  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        <AppliedRoute path="/" exact component={HomeRoute} props={childProps} />
        <AppliedRoute
          path="/login"
          exact
          component={Login}
          props={childProps}
        />
        <UserRoutes path="/user" component={UserRoutes} props={childProps} />
        <DCX path="/dcx" component={UserRoutes} props={childProps} />

        {/* admin users */}
        <AppliedRoute
          path="/admin/users"
          exact
          component={AdminUsersRoute}
          props={childProps}
        />
        <AppliedRoute
          path="/admin/users/edit/:userId"
          exact
          component={AdminUserEditRoute}
          props={childProps}
        />

        {/* admin groups */}
        <AppliedRoute
          path="/admin/usergroups"
          exact
          component={AdminUserGroupsRoute}
          props={childProps}
        />
        <AppliedRoute
          path="/admin/usergroups/edit/:groupId"
          exact
          component={EditUserGroupRoute}
          props={childProps}
        />

        {/* ConnX - firmware */}
        <AppliedRoute
          path="/connx/fw"
          exact
          component={FirmwareView}
          props={childProps}
        />

        {/* Connx - registered devices */}
        <AppliedRoute
          path="/connx/devices"
          exact
          component={RegisteredDevicesRoute}
          props={childProps}
        />
        <AppliedRoute
          path="/connx/devices/edit/:id"
          exact
          props={childProps}
          component={EditRegisteredDeviceRoute}
        />
        <AppliedRoute
          path="/connx/devices/edit/defaultConfig/:id"
          exact
          props={childProps}
          component={EditDefaultConfigRoute}
        />

        {/* Connx - device configuration */}
        <AppliedRoute
          path="/connx/config/defaults"
          exact
          component={ConfigurationView}
          props={childProps}
        />
        <AppliedRoute
          path="/connx/config/defaults/clone/:groupid/:cfgid/:versionid"
          exact
          props={childProps}
          component={CloneDeviceConfigurationRoute}
        />

        {/* Connx - RV models and orders */}
        <AppliedRoute
          path="/connx/rvmodels"
          exact
          component={RVModelView}
          props={childProps}
        />
        <AppliedRoute
          path="/connx/rvmodels/edit/model/:groupid/:modelid"
          exact
          component={RVModelEditRoute}
          props={childProps}
        />
        <AppliedRoute
          path="/connx/rvmodels/edit/order/:modelid/:orderid"
          exact
          component={RVModelOrderEditRoute}
          props={childProps}
        />

        {/* ConnX - IoT*/}
        <AppliedRoute
          path="/connx/iot/events"
          exact
          component={SystemEventView}
          props={childProps}
        />

        {/* Help */}
        <AppliedRoute path="/help" component={HelpRoutes} props={childProps} />

        {/* Retired, legacy, unused etc */}
        <AppliedRoute
          path="/maps/heatmap/:tripId"
          exact
          component={HeatMap}
          props={childProps}
        />

        {/* Finally, catch all unmatched routes */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
};

Routes.displayName = "RootRouter";

export default Routes;
